export interface PaletteColor {
  hex: string
  label: string
}

export const SKIN_TONE_PALETTE: PaletteColor[] = [
  { hex: '#FBEACD', label: 'Pale' },
  { hex: '#FDDBB4', label: 'Fair' },
  { hex: '#E8B88A', label: 'Light Medium' },
  { hex: '#D4956A', label: 'Medium' },
  { hex: '#C68642', label: 'Olive' },
  { hex: '#B5784A', label: 'Medium Dark' },
  { hex: '#8D5524', label: 'Dark' },
  { hex: '#5C3317', label: 'Very Dark' },
]

export const HAIR_COLOR_PALETTE: PaletteColor[] = [
  { hex: '#000000', label: 'Black' },
  { hex: '#3D2314', label: 'Dark Brown' },
  { hex: '#7C4A1A', label: 'Brown' },
  { hex: '#B5651D', label: 'Light Brown' },
  { hex: '#C8A96E', label: 'Dirty Blonde' },
  { hex: '#F5E642', label: 'Blonde' },
  { hex: '#C8102E', label: 'Red' },
  { hex: '#B0B0B0', label: 'Gray' },
  { hex: '#FFFFFF', label: 'White' },
]

export const BUST_SIZE_OPTIONS = ['Flat-Chested', 'Small', 'Medium', 'Large'] as const

export const BEARD_STYLE_OPTIONS = [
  'Full Beard',
  'Goatee',
  'Circle Beard',
  'Van Dyke',
  'Balbo',
  'Anchor Beard',
  'Garibaldi',
  'Verdi',
  'Mutton Chops',
  'Chin Strap',
] as const

export const FACE_SHAPE_OPTIONS = ['Oval', 'Round', 'Square', 'Heart', 'Oblong', 'Diamond'] as const

export const EYE_COLOR_OPTIONS = [
  'Dark Brown',
  'Brown',
  'Hazel',
  'Green',
  'Blue',
  'Gray',
  'Amber',
] as const

export interface ActorCharacterForm {
  characterName: string
  gender: 'Female' | 'Male' | ''
  ageGroup: string
  ethnicities: string[]
  skinTone: string
  bustSize: string
  bodyShape: string
  hairColor: string
  hairLength: string
  hairStyle: string
  beardStyle: string
  eyeColor: string
  faceShape: string
  expression: string
}

export const EMPTY_ACTOR_FORM: ActorCharacterForm = {
  characterName: '',
  gender: '',
  ageGroup: '',
  ethnicities: [],
  skinTone: '',
  bustSize: '',
  bodyShape: '',
  hairColor: '',
  hairLength: '',
  hairStyle: '',
  beardStyle: '',
  eyeColor: '',
  faceShape: '',
  expression: '',
}

const FEMALE_BODY_SHAPE_DESC: Record<string, string> = {
  Slim: 'slim slender figure with narrow waist and hips and flat stomach',
  Athletic: 'toned athletic figure with defined shoulders, firm flat stomach and lean legs',
  'Curvy (Hourglass)':
    'pronounced hourglass figure with upper body and hips significantly wider than a clearly defined narrow waist',
  Pear: 'pear-shaped figure with narrower shoulders and wider hips and thighs',
  'Plus Size': 'full rounded plus-size figure with generous curves throughout',
}

const MALE_BODY_SHAPE_DESC: Record<string, string> = {
  Slim: 'slim lean build with narrow shoulders and waist',
  Athletic: "lean runner's build with defined lean muscles, slim waist and endurance physique",
  Muscular:
    'powerfully built with large defined muscles, broad chest and thick arms from weight training',
  Average: 'average everyday build with a regular frame, neither notably muscular nor overweight',
  'Dad Bod': 'relaxed build with a soft rounded belly and less muscle definition',
  'Plus Size': 'full plump build with a rounded softer frame and fuller midsection',
}

const BUST_DESC: Record<string, string> = {
  'Flat-Chested': 'flat chest, the fitted top lies flat against the torso with no projection',
  Small: 'small chest fullness, the fitted top shows a subtle gentle curve at the chest',
  Medium:
    'moderate chest fullness, the fitted top shows a clearly rounded silhouette at the chest with a flat taut stomach',
  Large:
    'full chest that clearly fills and rounds the fitted top, narrow defined waist and flat taut stomach',
}

export function buildActorPrompt(form: ActorCharacterForm): string {
  const genderWord = form.gender === 'Female' ? 'woman' : form.gender === 'Male' ? 'man' : 'person'
  const ageLabel = form.ageGroup ? form.ageGroup.replace(/\s*\(.*?\)/g, '').trim() : ''
  const ethnicityStr = form.ethnicities.length > 0 ? form.ethnicities.join('/') : ''
  const subjectParts = [ageLabel, ethnicityStr, genderWord].filter(Boolean)
  const subject = subjectParts.join(' ')

  const BODY_SHAPE_DESC = form.gender === 'Male' ? MALE_BODY_SHAPE_DESC : FEMALE_BODY_SHAPE_DESC
  const bodyDesc = form.bodyShape ? (BODY_SHAPE_DESC[form.bodyShape] ?? '') : ''
  const bustDesc = form.gender === 'Female' && form.bustSize ? (BUST_DESC[form.bustSize] ?? '') : ''
  const proportionParts = [bodyDesc, bustDesc].filter(Boolean)

  const subjectSentence =
    proportionParts.length > 0
      ? `Photorealistic full-body studio photograph of a ${subject} with ${proportionParts.join(', ')}.`
      : `Photorealistic full-body studio photograph of a ${subject}.`

  const skinLabel = SKIN_TONE_PALETTE.find((c) => c.hex === form.skinTone)?.label ?? ''
  const hairColorLabel = HAIR_COLOR_PALETTE.find((c) => c.hex === form.hairColor)?.label ?? ''
  const hairParts = [hairColorLabel, form.hairLength, form.hairStyle].filter(Boolean)
  const hairDesc = hairParts.length > 0 ? `${hairParts.join(' ')} hair` : ''

  const appearanceParts = [
    skinLabel ? `${skinLabel} skin tone` : '',
    hairDesc,
    form.eyeColor ? `${form.eyeColor} eyes` : '',
    form.faceShape ? `${form.faceShape} face shape` : '',
    form.expression ? `${form.expression} expression` : '',
    form.gender === 'Male' && form.beardStyle ? `${form.beardStyle} beard` : '',
    form.characterName.trim() ? `character named ${form.characterName.trim()}` : '',
  ].filter(Boolean)
  const appearanceSentence = appearanceParts.length > 0 ? `${appearanceParts.join(', ')}.` : ''

  return [
    subjectSentence,
    appearanceSentence,
    'Wearing a tight-fitting mid-grey t-shirt that follows body contours and tight mid-grey shorts, barefoot.',
    'Full body shot from head to feet, entire body visible including feet on the ground. Standing upright arms relaxed at sides facing the camera directly, eye level, subject centred filling 80% of vertical frame.',
    'Soft even front-lit studio lighting, soft natural shadows, 35mm lens f/5.6, deep focus. Solid pure white seamless studio backdrop, no shadows or colour on the background.',
  ]
    .filter(Boolean)
    .join(' ')
}
